import * as React from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import TextField from '@mui/material/TextField'
import { useTranslation } from 'react-i18next'
import { Stack, Box, InputAdornment, SxProps, Typography } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { Moment } from 'moment'

import {
  toggleFilterByJoinDateDialog as closeDialog,
  updateFilter,
  addFilterWithValue,
} from 'redux/slices/FilterBuilder'
import { useAppSelector, useAppDispatch } from 'redux/Hook'
import { DEFAULT_DATE_FORMAT } from 'lib/participants/Constants'
import { getMappedFilters } from './Helper'
import { dialogActionsStyle } from 'components/Participants/Filters/Styles'

const dialogStyles: SxProps = {
  sx: {
    minWidth: {
      xs: 320,
      md: 900,
    },
    height: '35.5rem',
  },
}

type InitialState = {
  [key: string]: any
}

const initialState: InitialState = {
  joinDateBefore: null,
  joinDateAfter: null,
  joinDateMoreThan: '',
  joinDateLessThan: '',
}

const dateFields = ['joinDateBefore', 'joinDateAfter']

const FilterByJoinDate = () => {
  const [state, setState] = React.useState(initialState)

  const { selectedFilters, showJoinDateDialog } = useAppSelector(
    (state) => state.filterBuilder,
  )
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  React.useEffect(() => {
    /**
     * ⚠️ This keeps the local state
     * and slice state in sync.
     */
    const mappedKeys = Object.keys(initialState)

    const mappedInitialState: InitialState = {}

    mappedKeys.forEach((filterKey) => {
      const existingFilter = selectedFilters.find(
        (selectedFilter) => selectedFilter.type === filterKey,
      )

      if (existingFilter) {
        if (dateFields.includes(existingFilter.type as string)) {
          mappedInitialState[filterKey] = existingFilter.UIValue
        } else {
          mappedInitialState[filterKey] = existingFilter.value
        }

        return
      }

      mappedInitialState[filterKey] = null
    })

    setState({ ...mappedInitialState })
  }, [showJoinDateDialog])

  const handleDateChange = ({
    value,
    type,
  }: {
    value: Moment | null
    type: 'joinDateBefore' | 'joinDateAfter'
  }) => {
    setState((prevState) => ({ ...prevState, [type]: value }))
  }

  const handleDaysChange = ({
    target: { name, value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    /**
     * ⚠️ Allow only numbers!
     */
    setState((prevState) => ({
      ...prevState,
      [name]: value.replace(/[^\d]+/g, ''),
    }))
  }

  const handelClose = () => {
    dispatch(closeDialog())
  }

  const handleApply = () => {
    const mappedFilters = getMappedFilters(state)

    mappedFilters.forEach((filter) => {
      const existingFilterIndex = selectedFilters.findIndex(
        (selectedFilter) => selectedFilter.type === filter.type,
      )

      if (existingFilterIndex !== -1) {
        dispatch(
          updateFilter({
            item: filter,
            index: existingFilterIndex,
          }),
        )
      } else {
        dispatch(addFilterWithValue(filter))
      }
    })

    handelClose()
  }

  const daysAgoLabel = (
    <InputAdornment position="end">
      {t('filters.joinDate.daysAgo')}
    </InputAdornment>
  )

  return (
    <Dialog
      open={showJoinDateDialog}
      onClose={handelClose}
      aria-labelledby={t('filters.joinDate.heading')}
      PaperProps={dialogStyles}
    >
      <Box p={3} pt={4} pb={0}>
        <Typography variant="h5">{t('filters.joinDate.heading')}</Typography>

        <Typography variant="body1" pt={3} color="text.secondary">
          {t('filters.joinDate.description')}
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <Stack direction={{ md: 'row', xs: 'column' }}>
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            localeText={{
              nextMonth: t('datePicker.nextMonth'),
              previousMonth: t('datePicker.previousMonth'),
            }}
          >
            <DesktopDatePicker
              label={t('filters.joinDate.fieldDateBefore')}
              value={state.joinDateBefore}
              inputFormat={DEFAULT_DATE_FORMAT}
              onChange={(selectedDate) =>
                handleDateChange({
                  value: selectedDate,
                  type: 'joinDateBefore',
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  sx={{ width: '100%', mr: 1.5 }}
                />
              )}
            />
          </LocalizationProvider>

          <LocalizationProvider
            dateAdapter={AdapterMoment}
            localeText={{
              nextMonth: t('datePicker.nextMonth'),
              previousMonth: t('datePicker.previousMonth'),
            }}
          >
            <DesktopDatePicker
              label={t('filters.joinDate.fieldDateAfter')}
              value={state.joinDateAfter}
              inputFormat={DEFAULT_DATE_FORMAT}
              onChange={(selectedDate) =>
                handleDateChange({ value: selectedDate, type: 'joinDateAfter' })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  sx={{
                    width: '100%',
                    marginTop: {
                      md: 0,
                      xs: 3,
                    },
                    marginBottom: {
                      md: 0,
                      xs: 3,
                    },

                    ml: {
                      md: 1.5,
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Stack>

        <Stack pt={3} direction={{ md: 'row', xs: 'column' }}>
          <TextField
            name="joinDateMoreThan"
            label={t('filters.joinDate.fieldDaysMoreThan')}
            variant="standard"
            value={state.joinDateMoreThan}
            onChange={handleDaysChange}
            fullWidth
            sx={{ mr: 1.5 }}
            InputProps={{
              endAdornment: daysAgoLabel,
            }}
          />

          <TextField
            name="joinDateLessThan"
            label={t('filters.joinDate.fieldDaysLessThan')}
            variant="standard"
            value={state.joinDateLessThan}
            onChange={handleDaysChange}
            fullWidth
            sx={{
              ml: {
                md: 1.5,
              },
              marginTop: {
                md: 0,
                xs: 3,
              },
            }}
            InputProps={{
              endAdornment: daysAgoLabel,
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3.5, ...dialogActionsStyle }}>
        <Button onClick={handelClose} sx={{ mr: 1 }}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleApply} variant="contained">
          {t('filters.applyFilter')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FilterByJoinDate
